import { useEffect, useMemo, useRef, useState } from 'react';
import { formatDate, isOutOfRange, parseTypedDate, toValidDate } from '../utils/calendar';
import { useDismissOnOutside } from './useDismissOnOutside';
import { useFloatingPosition } from './useFloatingPosition';

const useDateField = ({ value, onChange, onBlur, minDate, maxDate, alwaysOpen = false }) => {
  const containerRef = useRef(null);
  const panelRef = useRef(null);
  const panelRefs = useMemo(() => [panelRef], []);
  const selected = useMemo(() => toValidDate(value), [value]);
  const [toggledOpen, setOpen] = useState(false);
  const open = alwaysOpen || toggledOpen;
  const [text, setText] = useState(formatDate(selected));
  const [viewDate, setViewDate] = useState(selected || new Date());

  useEffect(() => {
    setText(formatDate(selected));
    if (selected) setViewDate(selected);
  }, [selected]);

  useDismissOnOutside(containerRef, toggledOpen && !alwaysOpen, () => setOpen(false), panelRefs);
  useFloatingPosition(containerRef, panelRef, toggledOpen && !alwaysOpen);

  const commit = (date) => {
    setText(formatDate(date));
    onChange?.(date);
  };

  const handleSelect = (date) => {
    commit(date);
    setOpen(false);
  };

  const handleTextChange = (event) => {
    const typed = event.target.value;
    setText(typed);
    const parsed = parseTypedDate(typed);
    if (parsed && !isOutOfRange(parsed, minDate, maxDate)) {
      setViewDate(parsed);
      onChange?.(parsed);
    }
  };

  const handleBlur = () => {
    onBlur?.();
    if (text === '') {
      if (selected) onChange?.(null);
      return;
    }
    if (!parseTypedDate(text)) setText(formatDate(selected));
  };

  return {
    containerRef,
    panelRef,
    selected,
    open,
    text,
    viewDate,
    setOpen,
    setViewDate,
    handleSelect,
    handleTextChange,
    handleBlur,
    handleClear: () => handleSelect(null)
  };
};

export default useDateField;
